import React, { useEffect, useState } from "react";
import { catalogApi, userApi } from "../../api/auctiongate.js";

/**
 * A small grid of "reference catalogs" exposed by the plugin:
 *   - Shippers
 *   - Doc types
 *   - Fee table (remote)
 *   - States dictionary
 *   - Export data (doc types × auctions)
 *   - App links (mobile apps)
 */
const SOURCES = [
  { key: "shippers", title: "Shippers", fetch: () => catalogApi.shippers(), badge: "AG" },
  { key: "docTypes", title: "Document types", fetch: () => catalogApi.docTypes(), badge: "AG" },
  { key: "feeRemote", title: "Auction fee (remote)", fetch: () => catalogApi.feeRemote(), badge: "AG" },
  { key: "states", title: "States dictionary", fetch: () => userApi.states(), badge: "AG" },
  { key: "exportData", title: "Export data", fetch: () => userApi.exportData(), badge: "AG" },
  { key: "appLinks", title: "App links (mobile)", fetch: () => userApi.appLinks(), badge: "AG" },
];

export default function ReferenceLists() {
  return (
    <div className="grid grid-2">
      {SOURCES.map((s) => (
        <RefCard key={s.key} source={s} />
      ))}
    </div>
  );
}

function RefCard({ source }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await source.fetch();
      setData(r);
    } catch (e) {
      if (e?.status && e.status >= 400 && e.status < 500) setData({ __empty: true });
      else setErr(e?.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card">
      <div className="card__title">
        {source.title}
        <span className="badge">{source.badge}</span>
      </div>
      {loading && <span className="loader" />}
      {err && <div className="login-error small">{err}</div>}
      {!loading && !err && data && <Preview data={data} />}
      <button className="btn btn-ghost mt-2" onClick={load} disabled={loading} style={{ fontSize: 11, padding: "4px 10px" }}>
        Refresh
      </button>
    </div>
  );
}

function Preview({ data }) {
  if (data?.__empty) return <div className="placeholder-panel">No data.</div>;
  const arr = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : null;
  if (arr) {
    return (
      <div className="small" style={{ maxHeight: 180, overflow: "auto" }}>
        {arr.slice(0, 12).map((item, i) => (
          <div key={i} style={refRow}>
            {typeof item === "object" ? (
              <>
                <b>{item.name || item.title || item.company || item.state || item.code || `#${i + 1}`}</b>
                {item.value !== undefined && <span className="muted"> · {item.value}</span>}
                {item.link && (
                  <>
                    {" "}
                    · <a href={item.link} target="_blank" rel="noreferrer">link</a>
                  </>
                )}
              </>
            ) : (
              <span>{String(item)}</span>
            )}
          </div>
        ))}
        {arr.length > 12 && <div className="muted mt-2">…and {arr.length - 12} more</div>}
      </div>
    );
  }
  return (
    <pre style={{ fontSize: 11, maxHeight: 160, overflow: "auto", background: "var(--color-bg-card-elevated)", padding: 8, borderRadius: 6 }}>
      {JSON.stringify(data, null, 2).slice(0, 800)}
    </pre>
  );
}

const refRow = {
  padding: "4px 6px",
  borderBottom: "1px solid var(--color-border)",
};
